import { CommonModule, DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  OnInit,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import {
  PartnerContractListItemResponse,
  PartnerContractResponse,
} from '@interfaces/partners/contracts/responses/partner-contract.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerContractsHttpService } from '@services/http/partners/partner-contracts-http.service';
import { saveBlobAsFile } from '@src/app/core/utils/zip-download.utils';
import { ColumnDefinition } from '@src/app/shared/interfaces/column-definition.interface';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { finalize } from 'rxjs/operators';
import {
  backendErrorMessage,
  isPartnerContractFinalized,
  partnerContractNumber,
  partnerContractPartyTypeLabel,
  partnerContractStatusLabel,
} from '../../utils/partner-contract.utils';
import { ModalGeneratePartnerContractComponent } from './components/modal-generate-partner-contract/modal-generate-partner-contract.component';

/**
 * Aba "Contratos" do cadastro do parceiro (card 3462): as minutas do Contrato de Parceria
 * Comercial geradas para ele e o botão "Gerar contrato". Abrir uma linha leva ao editor.
 */
@Component({
  selector: 'app-partner-contracts',
  standalone: true,
  imports: [CommonModule, SharedModule, ModalGeneratePartnerContractComponent],
  templateUrl: './partner-contracts.component.html',
  styleUrls: ['./partner-contracts.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerContractsComponent implements OnInit {
  @Input({ required: true }) partnerId!: string;
  /** Nome do parceiro: padrão do "nome curto" na modal. */
  @Input() partnerName = '';
  /** E-mail do parceiro: padrão do e-mail do representante na modal. */
  @Input() partnerEmail = '';

  @ViewChild('generateModal') private generateModal?: ModalGeneratePartnerContractComponent;

  private readonly contractsService = inject(PartnerContractsHttpService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  readonly permissions = Permissions.PartnerContract;
  readonly contracts = signal<PartnerContractListItemResponse[]>([]);
  readonly loading = signal(true);

  readonly canCreate = this.permissionSignal(this.permissions.CREATE);
  readonly canView = this.permissionSignal(this.permissions.VIEW);
  readonly canExportPdf = this.permissionSignal(this.permissions.EXPORT_PDF);
  readonly canDelete = this.permissionSignal(this.permissions.DELETE);

  /** Só liga a coluna de ações quando há alguma ação possível (o `app-table` olha o binding). */
  readonly hasRowActions = computed(
    () => this.canView() || this.canExportPdf() || this.canDelete()
  );

  readonly columnDefinitions: ColumnDefinition[] = [
    // Número `PR-AAAAMM-seq` (card 3643); o `contractCode` antigo fica de reserva no template.
    new ColumnDefinition({
      field: 'contractNumber',
      header: 'Número',
      template: 'contractNumber',
      style: { width: '150px' },
    }),
    new ColumnDefinition({ field: 'title', header: 'Título' }),
    new ColumnDefinition({ field: 'partyType', header: 'Tipo', template: 'partyType' }),
    new ColumnDefinition({
      field: 'templateVersion',
      header: 'Modelo',
      template: 'templateVersion',
    }),
    new ColumnDefinition({ field: 'status', header: 'Situação', template: 'status' }),
    new ColumnDefinition({
      field: 'unfilledVariablesCount',
      header: 'Pendências',
      template: 'unfilledVariablesCount',
    }),
    new ColumnDefinition({ field: 'updatedAt', header: 'Atualizado em', format: 'datetime' }),
  ];

  readonly statusLabel = partnerContractStatusLabel;
  readonly partyTypeLabel = partnerContractPartyTypeLabel;
  readonly isFinalized = isPartnerContractFinalized;
  readonly contractNumber = partnerContractNumber;

  ngOnInit(): void {
    this.load();
  }

  openGenerateModal(): void {
    this.generateModal?.open({
      partnerId: this.partnerId,
      partnerName: this.partnerName,
      partnerEmail: this.partnerEmail,
    });
  }

  /** Minuta criada: vai direto para o editor. */
  onGenerated(contract: PartnerContractResponse): void {
    this.router.navigate(['/dashboard/partner/contracts', contract.id]);
  }

  openEditor(contract: PartnerContractListItemResponse): void {
    if (!this.canView()) return;
    this.router.navigate(['/dashboard/partner/contracts', contract.id]);
  }

  rowActions(contract: PartnerContractListItemResponse): MenuItem[] {
    const items: MenuItem[] = [];

    if (this.canView()) {
      items.push({
        label: 'Abrir no editor',
        icon: 'pi pi-pencil',
        route: ['/dashboard/partner/contracts', contract.id],
      } as MenuItem);
    }
    if (this.canExportPdf()) {
      items.push({
        label: 'Baixar PDF',
        icon: 'pi pi-file-pdf',
        command: () => this.downloadPdf(contract),
      });
    }
    if (this.canDelete() && !this.isFinalized(contract.status)) {
      items.push({
        label: 'Excluir',
        icon: 'pi pi-trash',
        command: () => this.confirmDelete(contract),
      });
    }

    return items;
  }

  private downloadPdf(contract: PartnerContractListItemResponse): void {
    this.contractsService
      .downloadContractPdf(contract.id, `${partnerContractNumber(contract)}.pdf`)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: file => saveBlobAsFile(this.document, file.blob, file.fileName),
        error: error =>
          this.toast.showToastError(
            'Não foi possível baixar o PDF',
            backendErrorMessage(error) || 'Tente de novo em instantes.'
          ),
      });
  }

  private confirmDelete(contract: PartnerContractListItemResponse): void {
    this.confirmationService
      .confirm(
        `Excluir o contrato ${partnerContractNumber(contract)}?`,
        'A minuta sai da lista do parceiro. O modelo e os outros contratos não mudam.',
        'danger',
        'Cancelar',
        'Excluir'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (!confirmed) return;
        this.contractsService
          .deleteContract(contract.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.toast.showToastSuccess('Contrato excluído', '');
              this.load();
            },
            error: error =>
              this.toast.showToastError(
                'Não foi possível excluir o contrato',
                backendErrorMessage(error) || 'Tente de novo em instantes.'
              ),
          });
      });
  }

  private load(): void {
    this.loading.set(true);
    this.contractsService
      .getContractsByPartner(this.partnerId)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: list => this.contracts.set(list),
        error: error =>
          this.toast.showToastError(
            'Não foi possível carregar os contratos do parceiro',
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  private permissionSignal(permission: string) {
    return toSignal(this.permissionsService.hasPermission(permission), { initialValue: false });
  }
}
